// Ancestor spine in the active thread view: the show payload carries an
// `ancestors` chain (root head post -> nearest parent answer) rendered above
// the thread head, with the initial scroll anchored past it so parent context
// is a scroll-up away instead of a click-climb per level.
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'

export const SCROLL_ANCESTORS_FEATURE = 'scroll_ancestors'

export const PRIVATE_ANCESTOR_ID = '__PRIVATE__'

export interface QuestAncestorUser {
    id?: string
    name?: string
    user_image_url?: string
    avatar_urls?: Record<string, string> | null
}

export interface QuestAncestorEntry {
    id: string
    quest_id?: string
    branch_quest_id?: string
    content_preview?: string
    branch_reply_count?: number
    is_root?: boolean
    deleted?: boolean
    created_at?: string
    user?: QuestAncestorUser
}

export function ancestorEntryNavigationFilter(entry: QuestAncestorEntry) {
    const questId = entry.branch_quest_id || entry.quest_id
    if (!questId || !entry.id || isPrivateAncestor(entry)) return null

    return { questId, answerId: entry.id }
}

export function scrollAncestorsEnabled(): boolean {
    return userHasFeature(SCROLL_ANCESTORS_FEATURE)
}

export function isPrivateAncestor(entry: QuestAncestorEntry): boolean {
    return entry?.id === PRIVATE_ANCESTOR_ID
}

// A placeholder rung the viewer may see whose post was deleted.
export function isDeletedAncestor(entry: QuestAncestorEntry): boolean {
    return isPrivateAncestor(entry) && entry.deleted === true
}

export function questNeedsAncestorHydration(quest: unknown): boolean {
    return !!quest && !Array.isArray((quest as { ancestors?: unknown }).ancestors)
}

// Entries arrive ordered root -> nearest and render in that order. Private
// rungs keep their slot (locked row) so the chain reads as an unbroken path.
export function resolveQuestAncestors(quest: unknown): QuestAncestorEntry[] {
    const ancestors = (quest as { ancestors?: unknown })?.ancestors
    if (!Array.isArray(ancestors)) return []

    return ancestors.filter(
        (entry): entry is QuestAncestorEntry =>
            !!entry &&
            typeof entry === 'object' &&
            typeof (entry as { id?: unknown }).id === 'string',
    )
}

export function questHasAncestorSpine(quest: unknown): boolean {
    return scrollAncestorsEnabled() && resolveQuestAncestors(quest).length > 0
}
